import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Instagram, Youtube, Facebook } from 'lucide-react';
import api from '../lib/api';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [social, setSocial] = useState({});

  useEffect(() => {
    api.get('/settings').then(({ data }) => setSocial(data.social || {})).catch(() => {});
  }, []);

  const navigationLinks = [
    { name: 'Products', path: '/products' },
    { name: 'Categories', path: '/categories' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Sell with OrvDigital', path: '/sell' },
    { name: 'FAQ', path: '/faq' }
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'Refund Policy', path: '/refund' },
    { name: 'License Terms', path: '/license-terms' },
    { name: 'Partner Terms', path: '/partner-terms' },
    { name: 'Disclaimer', path: '/disclaimer' }
  ];

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: social.instagram },
    { name: 'YouTube', icon: Youtube, url: social.youtube },
    { name: 'Facebook', icon: Facebook, url: social.facebook }
  ].filter((item) => /^https?:\/\//i.test(item.url || ''));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <footer className="bg-gradient-to-b from-[#0B0B0D] to-[#050506] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-10 md:mb-12"
        >
          {/* Brand Section */}
          <motion.div variants={itemVariants} className="col-span-2 lg:col-span-1 space-y-4 md:space-y-6">
            <Link to="/" className="inline-block">
              <img
                src="/assets/orvdigital-logo-white.png"
                alt="OrvDigital"
                className="h-9 md:h-10 w-auto"
              />
            </Link>
            <div className="space-y-1 md:space-y-2">
              <p className="text-sm text-gray-400">Premium AI & Digital Resources</p>
              <p className="text-base md:text-lg font-medium text-white tracking-wide">
                CREATE • BUILD • GROW
              </p>
            </div>
          </motion.div>

          {/* Navigation Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-white font-semibold mb-4">Navigation</h3>
            <ul className="space-y-3">
              {navigationLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-200 inline-block group"
                  >
                    <span className="relative">
                      {link.name}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-white group-hover:w-full transition-all duration-300"></span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-200 inline-block group"
                  >
                    <span className="relative">
                      {link.name}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-white group-hover:w-full transition-all duration-300"></span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-white font-semibold mb-4">Connect</h3>
            <div className="flex space-x-4">
              {socialLinks.length ? socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-300 group"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.name}
                  >
                    <Icon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors duration-200" />
                  </motion.a>
                );
              }) : (
                <p className="text-sm text-gray-500">Follow OrvDigital—links coming soon.</p>
              )}
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="pt-8 border-t border-white/5 text-center"
        >
          <p className="text-sm text-gray-500">
            © {currentYear} OrvDigital. All rights reserved.
          </p>
          <p className="mt-2 text-xs text-gray-600">
            Owned and operated by Nitesh Kumar Vishwakarma, India.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};
